import React, { useEffect, useRef, useState } from 'react'

import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'

import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
} from '@chakra-ui/react'

import { toast } from 'react-toastify'

import { Textarea } from '@/components/Form'

import SimpleFileInput from '@/components/Form/SimpleFileInput'

import { useOfferCostBudget } from '@/features/offerCostBudgets/useOfferCostBudget'
import useUser from '@/features/users/useUser'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { hideModal } from '@/stores/ui/modalSlice'
import { filterBySelectedIndices, normalizeUserIdList } from '@/utils/arrayHelper'
import { isNullish } from '@/utils/helper'
import { SourcingPostType } from '@eapproval/offer-shared'
import { createColumnHelper } from '@tanstack/react-table'
import RFQModalVendorTable from './RFQModalVendorTable'

const RFQModal = ({
  title = '',
  offerId,
  costBudgetId,
  tenderDocumentIndex,
  vendorDetails = [],
  technicalUsers = [],
  procurementOfficer,
  ccList = [],
  requestType: initialRequestType,
  canChangePostType,
  initialRemark = '',
  initialExtraDocuments = [],
}) => {
  const [selectedVendors, setSelectedVendors] = useState({})
  const [requestType, setRequestType] = useState(initialRequestType ?? '')
  const formIdentityKey = `${offerId}:${costBudgetId ?? ''}:${tenderDocumentIndex ?? ''}`

  const columnHelper = createColumnHelper()
  const dispatch = useDispatch()
  const methods = useForm({
    defaultValues: {
      remark: initialRemark,
      extraDocuments: initialExtraDocuments,
    },
  })
  const { reset } = methods
  const initialFormValuesRef = useRef({
    remark: initialRemark,
    extraDocuments: initialExtraDocuments,
  })
  const initialRequestTypeRef = useRef(initialRequestType ?? '')

  initialFormValuesRef.current = {
    remark: initialRemark,
    extraDocuments: initialExtraDocuments,
  }
  initialRequestTypeRef.current = initialRequestType ?? ''

  const { useFetchAllUsers } = useUser()
  const { useSendRFQ } = useOfferCostBudget()
  const sendRFQ = useSendRFQ()

  const technicalUserIds = normalizeUserIdList(technicalUsers)
  const ccUserIds = normalizeUserIdList(ccList)

  const { data: fetchUsersResponse } = useFetchAllUsers({
    page: 1,
    pageSize: 0,
    sortBy: 'fullName',
    orderBy: 1,
    filters: { status: 'active' },
  })

  const procurementOfficerUser = Array.isArray(fetchUsersResponse?.data)
    ? fetchUsersResponse.data.find((user) => user._id === procurementOfficer)
    : null

  const technicalUserObjects = Array.isArray(fetchUsersResponse?.data)
    ? fetchUsersResponse.data.filter((user) => technicalUserIds.includes(user._id))
    : []

  const ccUserObjects = Array.isArray(fetchUsersResponse?.data)
    ? fetchUsersResponse.data.filter((user) => ccUserIds.includes(user._id))
    : []

  useEffect(() => {
    if (vendorDetails) {
      setSelectedVendors(
        vendorDetails.reduce((prev, _, index) => {
          prev[index] = true
          return prev
        }, {})
      )
    }
  }, [vendorDetails])

  useEffect(() => {
    reset(initialFormValuesRef.current)
    setRequestType(initialRequestTypeRef.current)
  }, [formIdentityKey, reset])

  const closeModal = () => {
    dispatch(hideModal())
  }
  const onSubmit = async (data) => {
    if (!offerId) {
      toast.error('Wrong offerId')
      return
    }

    if (!costBudgetId) {
      toast.error('Wrong costBudgetId')
      return
    }
    if (data?.extraDocuments.length > 10) {
      toast.error('Please select less than 10 files')
      return
    }

    try {
      dispatch(showLoadingOverlay())

      const selectedVendor = filterBySelectedIndices(vendorDetails, selectedVendors)
        .map((vendorDetail) => vendorDetail.vendor)
        .filter((vendor) => !isNullish(vendor))

      if (selectedVendor.length === 0) {
        throw new Error('Please select at least one valid vendor with email address  ')
      }

      await sendRFQ.mutateAsync({
        offerId: offerId,
        costBudgetId: costBudgetId,
        tenderDocumentIndex: tenderDocumentIndex,
        remark: data.remark ?? '',
        vendors: selectedVendor,
        extraDocuments: data.extraDocuments,
        sourcingPostType: requestType,
      })

      closeModal()
    } catch (error) {
      console.error(`Error from Post RFQ` + error)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  return (
    <Modal isOpen={true} onClose={closeModal} size="xl">
      <ModalOverlay />
      <ModalContent maxW={'80%'}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ModalHeader>{title}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Flex direction="column" gap="3">
                <Flex gap="3">
                  <Text as="b">Procurement Officer: </Text>
                  <Text>
                    {procurementOfficerUser?.fullName ?? ''} ({procurementOfficerUser?.email ?? ''})
                  </Text>
                </Flex>
                <Flex gap="3">
                  <Text as="b">Technical: </Text>

                  {technicalUserObjects.map((user, index) => (
                    <Text key={index}>
                      {user?.fullName ?? ''} ({user?.email ?? ''})
                    </Text>
                  ))}
                </Flex>

                <Flex gap="1">
                  <Text as="i">The RFQ email will be cc to: </Text>
                  {ccUserObjects.map((user, index) => (
                    <Text key={index} as="i">
                      {user?.fullName ?? ''} ({user?.email ?? ''})
                    </Text>
                  ))}
                  <Text as="i">and RFQ-SIB.hk@siemens.com</Text>
                </Flex>

                <RFQModalVendorTable
                  vendorDetails={vendorDetails}
                  selectedVendors={selectedVendors}
                  setSelectedVendors={setSelectedVendors}
                />

                <FormControl as={Flex}>
                  <FormLabel w="30%">Request Type</FormLabel>
                  <Select
                    value={requestType}
                    onChange={(e) => {
                      setRequestType(e.target.value)
                    }}
                    placeholder="Select"
                    disabled={!canChangePostType}
                  >
                    <option value={SourcingPostType.REQUEST_FOR_QUOTATION}>Request For Quotation</option>
                    <option value={SourcingPostType.REQUEST_FOR_TECHNICAL_SUPPORT_DOCUMENT}>
                      Request For Technical Support Document
                    </option>
                    <option value={SourcingPostType.REQUEST_FOR_UPDATE_OF_TERMS}>Request For Update of terms </option>
                    <option value={SourcingPostType.REQUEST_FOR_CLARIFICATION}>Request For Clarification</option>
                    <option value={SourcingPostType.REQUEST_FOR_DETAILED_TABLE}>Request For Detailed Table</option>
                  </Select>
                </FormControl>

                <Textarea name="remark" label={'Comments'} rows="5" isRequired={false} />

                <SimpleFileInput name="extraDocuments" label={'Extra Documents'} isMultiple isRequired={false} isRow />
              </Flex>
            </ModalBody>
            <ModalFooter>
              <HStack>
                <Button colorScheme={'green'} type="submit">
                  Send
                </Button>
                <Button colorScheme="gray" onClick={closeModal}>
                  Close
                </Button>
              </HStack>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  )
}

export default RFQModal
